import React, { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from '@heroicons/react/24/outline'

import { updateConfig, addService, updateService, deleteService, moveService, updateGroupName, getConfigByType } from '../api/config'
import LoadingSpinner from '../components/LoadingSpinner'
import ServiceEditModal from '../components/ServiceEditModal'
import GroupEditModal from '../components/GroupEditModal'

// 渲染服务图标的函数
const renderServiceIcon = (icon) => {
  if (!icon) return null

  // 如果是完整URL图片
  if (icon.startsWith('http')) {
    return (
      <img
        src={icon}
        alt=""
        className="w-5 h-5 rounded object-cover"
        onError={(e) => {
          e.target.style.display = 'none'
        }}
      />
    )
  }

  // 如果是图标库图标（mdi-, si-, di-）
  if (icon.startsWith('mdi-') || icon.startsWith('si-') || icon.startsWith('di-')) {
    return (
      <div className="w-5 h-5 flex items-center justify-center text-gray-300 bg-gray-600 rounded">
        <span className="text-xs">📱</span>
      </div>
    )
  }

  // 如果是本地图标路径
  if (icon.startsWith('/icons/')) {
    return (
      <img
        src={icon}
        alt=""
        className="w-5 h-5 rounded object-cover"
        onError={(e) => {
          e.target.style.display = 'none'
        }}
      />
    )
  }

  // 默认图标
  return (
    <div className="w-5 h-5 flex items-center justify-center text-gray-300 bg-gray-600 rounded">
      <span className="text-xs">🔗</span>
    </div>
  )
}

const ServicesConfig = ({ config, settings }) => {
  const [expandedGroups, setExpandedGroups] = useState(new Set())
  const [editingService, setEditingService] = useState(null)
  const [showServiceModal, setShowServiceModal] = useState(false)
  const [showGroupModal, setShowGroupModal] = useState(false)
  const [editingGroup, setEditingGroup] = useState(null)
  // 添加选中状态管理
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [selectedService, setSelectedService] = useState(null)

  const queryClient = useQueryClient()

  // 键盘快捷键支持
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleClearSelection()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  // 添加服务的 mutation
  const addServiceMutation = useMutation({
    mutationFn: ({ groupName, service }) => addService(groupName, service),
    onSuccess: () => {
      queryClient.invalidateQueries(['config'])
      toast.success('服务已添加')
    },
    onError: (error) => {
      toast.error(`添加失败: ${error.message}`)
    },
  })

  // 更新服务的 mutation
  const updateServiceMutation = useMutation({
    mutationFn: ({ groupName, serviceIndex, service }) => updateService(groupName, serviceIndex, service),
    onSuccess: () => {
      queryClient.invalidateQueries(['config'])
      toast.success('服务已更新')
    },
    onError: (error) => {
      toast.error(`更新失败: ${error.message}`)
    },
  })

  // 删除服务的 mutation
  const deleteServiceMutation = useMutation({
    mutationFn: ({ groupName, serviceIndex }) => deleteService(groupName, serviceIndex),
    onSuccess: () => {
      queryClient.invalidateQueries(['config'])
      toast.success('服务已删除')
    },
    onError: (error) => {
      toast.error(`删除失败: ${error.message}`)
    },
  })

  // 移动服务的 mutation
  const moveServiceMutation = useMutation({
    mutationFn: ({ groupName, serviceIndex, direction }) => moveService(groupName, serviceIndex, direction),
    onSuccess: () => {
      queryClient.invalidateQueries(['config'])
      toast.success('服务顺序已更新')
    },
    onError: (error) => {
      toast.error(`移动失败: ${error.message}`)
    },
  })

  // 更新分组名称的 mutation
  const updateGroupMutation = useMutation({
    mutationFn: ({ oldGroupName, newGroupName, layoutConfig }) => updateGroupName(oldGroupName, newGroupName, layoutConfig),
    onSuccess: () => {
      queryClient.invalidateQueries(['config'])
      toast.success('分组已更新')
    },
    onError: (error) => {
      toast.error(`更新失败: ${error.message}`)
    },
  })

  // 更新配置的 mutation
  const updateConfigMutation = useMutation({
    mutationFn: (data) => updateConfig('services', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['config'])
      toast.success('分组顺序已更新')
    },
    onError: (error) => {
      toast.error(`更新顺序失败: ${error.message}`)
    },
  })

  const toggleGroup = (groupName) => {
    const newExpanded = new Set(expandedGroups)
    if (newExpanded.has(groupName)) {
      newExpanded.delete(groupName)
    } else {
      newExpanded.add(groupName)
    }
    setExpandedGroups(newExpanded)
  }

  // 处理分组选中
  const handleSelectGroup = (groupName, event) => {
    event.stopPropagation()
    if (selectedGroup === groupName) {
      setSelectedGroup(null)
    } else {
      setSelectedGroup(groupName)
      setSelectedService(null) // 清除服务选中状态
    }
  }

  // 处理服务选中
  const handleSelectService = (groupName, serviceIndex, event) => {
    event.stopPropagation()
    const serviceKey = `${groupName}-${serviceIndex}`
    if (selectedService === serviceKey) {
      setSelectedService(null)
    } else {
      setSelectedService(serviceKey)
      setSelectedGroup(null) // 清除分组选中状态
    }
  }

  const handleDeleteService = (groupName, serviceIndex) => {
    if (confirm('确定要删除这个服务吗？')) {
      deleteServiceMutation.mutate({ groupName, serviceIndex })
    }
  }

  const handleMoveService = (groupName, serviceIndex, direction) => {
    // 设置当前服务为选中状态
    const currentServiceKey = `${groupName}-${serviceIndex}`
    setSelectedService(currentServiceKey)

    moveServiceMutation.mutate({ groupName, serviceIndex, direction }, {
      onSuccess: () => {
        // 移动后，选中状态跟随到新位置
        const newIndex = direction === 'up' ? serviceIndex - 1 : serviceIndex + 1
        const newServiceKey = `${groupName}-${newIndex}`
        setSelectedService(newServiceKey)
      }
    })
  }

  const handleEditService = (groupName, serviceIndex, service) => {
    setEditingService({
      groupName,
      serviceIndex,
      service: { ...service },
    })
    setShowServiceModal(true)
  }

  const handleAddGroup = () => {
    setEditingGroup(null)
    setShowGroupModal(true)
  }

  const handleEditGroup = (groupName) => {
    setEditingGroup(groupName)
    setShowGroupModal(true)
  }

  const handleAddService = (groupName = null) => {
    setEditingService({
      groupName: groupName || (config.length > 0 ? Object.keys(config[0])[0] : ''),
      serviceIndex: -1,
      service: null
    })
    setShowServiceModal(true)
  }

  const handleSaveService = (groupName, serviceData) => {
    if (editingService.serviceIndex === -1) {
      // 添加新服务
      addServiceMutation.mutate({ groupName, service: serviceData })
    } else {
      // 检查是否更改了分组
      if (groupName !== editingService.groupName) {
        // 需要在分组间移动服务：先删除旧组的服务，然后在新组添加
        // 使用 Promise 链式调用确保先删除后添加
        deleteServiceMutation.mutate(
          {
            groupName: editingService.groupName,
            serviceIndex: editingService.serviceIndex
          },
          {
            onSuccess: () => {
              // 删除成功后，在新组中添加服务
              addServiceMutation.mutate({ groupName, service: serviceData })
            }
          }
        )
      } else {
        // 在同一分组内更新服务
        updateServiceMutation.mutate({
          groupName: editingService.groupName,
          serviceIndex: editingService.serviceIndex,
          service: serviceData
        })
      }
    }
  }

  const handleSaveGroup = (groupName, groupConfig) => {
    if (editingGroup) {
      // 编辑现有分组 - 更新分组名称和布局配置
      updateGroupMutation.mutate({
        oldGroupName: editingGroup,
        newGroupName: groupName,
        layoutConfig: groupConfig
      })
    } else {
      // 添加新分组
      const newConfig = [...config]
      const newGroup = { [groupName]: [] }
      newConfig.push(newGroup)

      updateConfigMutation.mutate(newConfig, {
        onSuccess: () => {
          // 同时保存布局配置到 settings
          updateGroupLayoutConfig(groupName, groupConfig).then(() => {
            toast.success('分组已添加')
          }).catch((error) => {
            console.error('保存布局配置失败:', error)
            // 即使布局配置保存失败，分组已经添加成功
          })
        }
      })
    }
  }

  // 更新分组布局配置的函数
  const updateGroupLayoutConfig = async (groupName, layoutConfig) => {
    try {
      const settingsResponse = await getConfigByType('settings')
      const settingsData = settingsResponse.data || {}

      if (!settingsData.layout) {
        settingsData.layout = {}
      }

      settingsData.layout[groupName] = layoutConfig

      await updateConfig('settings', settingsData)
      console.log(`✅ 已更新分组 "${groupName}" 的布局配置:`, layoutConfig)
    } catch (error) {
      console.error('❌ 更新布局配置失败:', error)
      throw error
    }
  }

  // 处理分组上下移动
  const moveGroup = (index, direction) => {
    // 安全检查
    if (!config || !Array.isArray(config)) {
      toast.error('配置数据无效')
      return
    }

    if (index < 0 || index >= config.length) {
      toast.error('无效的分组索引')
      return
    }

    const newConfig = [...config]
    const targetIndex = direction === 'up' ? index - 1 : index + 1

    if (targetIndex >= 0 && targetIndex < newConfig.length) {
      // 确保两个索引位置的数据都存在
      if (!newConfig[index] || !newConfig[targetIndex]) {
        toast.error('分组数据缺失')
        return
      }

      // 获取当前分组名称并设置为选中状态
      const currentGroupName = Object.keys(newConfig[index])[0]
      setSelectedGroup(currentGroupName)

      // 安全的交换操作
      const temp = newConfig[index]
      newConfig[index] = newConfig[targetIndex]
      newConfig[targetIndex] = temp

      // 直接调用 updateConfig API
      updateConfig('services', newConfig).then(() => {
        queryClient.invalidateQueries(['config'])
        toast.success('分组顺序已更新')

        // 移动后保持选中状态
        setSelectedGroup(currentGroupName)
      }).catch((error) => {
        toast.error(`更新顺序失败: ${error.message}`)
      })
    } else {
      toast.error('无法移动到指定位置')
    }
  }

  // 获取所有分组名称
  const availableGroups = config.map(group => Object.keys(group)[0])

  // 获取当前编辑分组的布局配置
  const getCurrentGroupLayout = (groupName) => {
    if (!settings?.layout || !groupName) return null
    return settings.layout[groupName] || null
  }

  if (!config) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // 清除选中状态
  const handleClearSelection = () => {
    setSelectedGroup(null)
    setSelectedService(null)
  }

  return (
    <div className="space-y-6" onClick={handleClearSelection}>
      {/* 页面标题和操作 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">服务配置</h1>
          <p className="mt-2 text-gray-400">
            管理导航页上的服务和应用程序
          </p>
          <p className="mt-1 text-xs text-gray-500">
            💡 点击分组或服务项可选中并高亮显示，按 ESC 键或点击空白区域取消选中
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleAddGroup}
            className="btn-secondary"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            添加分组
          </button>
          <button
            onClick={() => handleAddService()}
            className="btn-primary"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            添加服务
          </button>
        </div>
      </div>

      {/* 服务列表 */}
      <div className="space-y-4">
        {config.map((group, groupIndex) => {
          const groupName = Object.keys(group)[0]
          const services = group[groupName] || []
          const isExpanded = expandedGroups.has(groupName)

          const isGroupSelected = selectedGroup === groupName

          return (
            <div
              key={groupIndex}
              className={`card transition-all duration-300 ${isGroupSelected
                ? 'ring-2 ring-blue-400 bg-gray-800/80 enhanced-glow-blue glow-selected-group'
                : 'hover:bg-gray-800/50'
                }`}
              onClick={(e) => handleSelectGroup(groupName, e)}
            >
              <div className="flex items-center justify-between">
                <div
                  className="flex items-center flex-1 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleGroup(groupName)
                  }}
                >
                  {isExpanded ? (
                    <ChevronDownIcon className="h-5 w-5 text-gray-400 mr-2" />
                  ) : (
                    <ChevronRightIcon className="h-5 w-5 text-gray-400 mr-2" />
                  )}
                  {(() => {
                    const groupLayout = getCurrentGroupLayout(groupName)
                    const groupIcon = groupLayout?.icon
                    if (!groupIcon) return null
                    return (
                      <div className="mr-2 flex-shrink-0">
                        {renderServiceIcon(groupIcon)}
                      </div>
                    )
                  })()}
                  <h3 className="text-lg font-semibold text-white">
                    {groupName}
                  </h3>
                  <span className="ml-3 badge-info">
                    {services.length} 个服务
                  </span>
                  {(() => {
                    const groupLayout = getCurrentGroupLayout(groupName)
                    const columns = groupLayout?.columns || 1
                    return (
                      <span className="ml-2 badge-secondary">
                        {columns} 列
                      </span>
                    )
                  })()}
                  {(() => {
                    const groupLayout = getCurrentGroupLayout(groupName)
                    if (!groupLayout) return null
                    const styleLabel = groupLayout.style === 'column' ? '布局: 列' : '布局: 行'
                    return (
                      <>
                        <span className="ml-2 badge-secondary">{styleLabel}</span>
                        {groupLayout.description ? (
                          <span className="ml-3 text-sm text-gray-400 truncate">{groupLayout.description}</span>
                        ) : null}
                      </>
                    )
                  })()}
                </div>
                <div className="flex items-center space-x-2">
                  {/* 移动按钮 */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      moveGroup(groupIndex, 'up')
                    }}
                    disabled={groupIndex === 0}
                    className="p-2 text-gray-400 hover:text-white hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ArrowUpIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      moveGroup(groupIndex, 'down')
                    }}
                    disabled={groupIndex === config.length - 1}
                    className="p-2 text-gray-400 hover:text-white hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ArrowDownIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleEditGroup(groupName)
                    }}
                    className="p-2 text-gray-400 hover:text-white hover:bg-gray-600 rounded-lg transition-colors"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleAddService(groupName)
                    }}
                    className="btn-secondary btn-sm"
                  >
                    <PlusIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {isExpanded && (
                <div className="mt-4">
                  {(() => {
                    // 获取当前分组的布局配置
                    const groupLayout = getCurrentGroupLayout(groupName)
                    const columns = groupLayout?.columns || 1

                    // 调试信息
                    console.log(`🎯 分组 "${groupName}" 布局配置:`, {
                      groupLayout,
                      columns,
                      settingsAvailable: !!settings,
                      layoutAvailable: !!settings?.layout
                    })

                    // 根据列数生成网格类名
                    const getGridClass = (cols) => {
                      const gridClasses = {
                        1: 'grid-cols-1',
                        2: 'grid-cols-1 md:grid-cols-2',
                        3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
                        4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
                        5: 'grid-cols-1 md:grid-cols-3 lg:grid-cols-5',
                        6: 'grid-cols-1 md:grid-cols-3 lg:grid-cols-6',
                        7: 'grid-cols-1 md:grid-cols-4 lg:grid-cols-7',
                        8: 'grid-cols-1 md:grid-cols-4 lg:grid-cols-8'
                      }
                      const gridClass = gridClasses[cols] || 'grid-cols-1'
                      console.log(`📐 分组 "${groupName}" 使用 ${cols} 列布局，CSS类: ${gridClass}`)
                      return gridClass
                    }

                    return (
                      <div className={`grid ${getGridClass(columns)} gap-3`}>
                        {services.map((service, serviceIndex) => {
                          const serviceName = Object.keys(service)[0]
                          const serviceConfig = service[serviceName]
                          const serviceKey = `${groupName}-${serviceIndex}`
                          const isServiceSelected = selectedService === serviceKey

                          return (
                            <div
                              key={serviceIndex}
                              className={`rounded-lg p-4 border transition-all duration-300 cursor-pointer ${isServiceSelected
                                ? 'bg-gray-600 border-green-400 ring-2 ring-green-400 enhanced-glow-green glow-selected-service'
                                : 'bg-gray-700 border-gray-600 hover:bg-gray-600 hover:border-gray-500'
                                }`}
                              onClick={(e) => handleSelectService(groupName, serviceIndex, e)}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center min-w-0 flex-1">
                                  {/* 服务图标 */}
                                  <div className="mr-3 flex-shrink-0">
                                    {renderServiceIcon(serviceConfig.icon)}
                                  </div>
                                  <h4 className="text-white font-medium truncate">
                                    {serviceName}
                                  </h4>
                                  {serviceConfig.href && (
                                    <a
                                      href={serviceConfig.href}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="ml-2 text-primary-400 hover:text-primary-300 flex-shrink-0"
                                    >
                                      <EyeIcon className="h-4 w-4" />
                                    </a>
                                  )}
                                </div>
                                <div className="flex items-center space-x-1 flex-shrink-0">
                                  {/* 移动按钮 */}
                                  <button
                                    onClick={() => handleMoveService(groupName, serviceIndex, 'up')}
                                    disabled={serviceIndex === 0}
                                    className="p-1 text-gray-400 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="上移"
                                  >
                                    <ArrowUpIcon className="h-3 w-3" />
                                  </button>
                                  <button
                                    onClick={() => handleMoveService(groupName, serviceIndex, 'down')}
                                    disabled={serviceIndex === services.length - 1}
                                    className="p-1 text-gray-400 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="下移"
                                  >
                                    <ArrowDownIcon className="h-3 w-3" />
                                  </button>
                                  <button
                                    onClick={() => handleEditService(groupName, serviceIndex, service)}
                                    className="p-1 text-gray-400 hover:text-white transition-colors"
                                    title="编辑"
                                  >
                                    <PencilIcon className="h-3 w-3" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteService(groupName, serviceIndex)}
                                    className="p-1 text-gray-400 hover:text-red-400 transition-colors"
                                    title="删除"
                                  >
                                    <TrashIcon className="h-3 w-3" />
                                  </button>
                                </div>
                              </div>

                              <div className="space-y-1">
                                <p className="text-sm text-gray-400">
                                  {serviceConfig.description || '无描述'}
                                </p>
                                {serviceConfig.href && (
                                  <p className="text-xs text-gray-500 truncate">
                                    {serviceConfig.href}
                                  </p>
                                )}
                              </div>
                            </div>
                          )
                        })}

                        {services.length === 0 && (
                          <div className="col-span-full text-center py-8 text-gray-400">
                            <p>此分组暂无服务</p>
                            <button
                              onClick={() => handleAddService(groupName)}
                              className="mt-2 btn-primary btn-sm"
                            >
                              添加第一个服务
                            </button>
                          </div>
                        )}
                      </div>
                    )
                  })()}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {config.length === 0 && (
        <div className="card text-center py-12">
          <h3 className="text-lg font-medium text-gray-300 mb-2">
            暂无服务配置
          </h3>
          <p className="text-gray-400 mb-4">
            开始添加你的第一个服务分组
          </p>
          <button
            onClick={handleAddGroup}
            className="btn-primary"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            创建分组
          </button>
        </div>
      )}

      {/* 小部件类型说明 */}
      <div className="card">
        <h3 className="card-title">可用的小部件类型</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-4">
          <div className="space-y-3">
            <h4 className="font-semibold text-blue-300 text-lg">📊 系统监控</h4>
            <div>
              <h5 className="font-medium text-white">系统资源 (resources)</h5>
              <p className="text-sm text-gray-400">显示 CPU、内存、磁盘使用情况</p>
            </div>
            <div>
              <h5 className="font-medium text-white">系统监控 (glances)</h5>
              <p className="text-sm text-gray-400">集成 Glances 系统监控</p>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="font-semibold text-green-300 text-lg">📺 媒体服务器</h4>
            <div>
              <h5 className="font-medium text-white">EMBY (emby)</h5>
              <p className="text-sm text-gray-400">EMBY 媒体服务器监控和控制</p>
            </div>
            <div>
              <h5 className="font-medium text-white">Jellyfin (jellyfin)</h5>
              <p className="text-sm text-gray-400">Jellyfin 媒体服务器监控和控制</p>
            </div>
            <div>
              <h5 className="font-medium text-white">Plex (plex)</h5>
              <p className="text-sm text-gray-400">Plex 媒体服务器监控和控制</p>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="font-semibold text-red-300 text-lg">⬇️ 下载工具</h4>
            <div>
              <h5 className="font-medium text-white">qBittorrent</h5>
              <p className="text-sm text-gray-400">qBittorrent 下载器监控</p>
            </div>
            <div>
              <h5 className="font-medium text-white">Transmission</h5>
              <p className="text-sm text-gray-400">Transmission 下载器监控</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
          <div className="space-y-3">
            <h4 className="font-semibold text-orange-300 text-lg">🛡️ 网络安全</h4>
            <div>
              <h5 className="font-medium text-white">Pi-hole</h5>
              <p className="text-sm text-gray-400">DNS 广告过滤服务</p>
            </div>
            <div>
              <h5 className="font-medium text-white">AdGuard Home</h5>
              <p className="text-sm text-gray-400">网络广告和跟踪器拦截</p>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="font-semibold text-indigo-300 text-lg">🌤️ 天气服务</h4>
            <div>
              <h5 className="font-medium text-white">Open-Meteo</h5>
              <p className="text-sm text-gray-400">免费开源天气 API</p>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="font-semibold text-yellow-300 text-lg">🔧 工具小部件</h4>
            <div>
              <h5 className="font-medium text-white">搜索/时钟/问候语</h5>
              <p className="text-sm text-gray-400">基础功能小部件</p>
            </div>
            <div>
              <h5 className="font-medium text-white">书签/Logo</h5>
              <p className="text-sm text-gray-400">导航和品牌展示</p>
            </div>
            <div>
              <h5 className="font-medium text-white">Bilibili/自定义API</h5>
              <p className="text-sm text-gray-400">特殊功能和扩展</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">

          <div className="space-y-3">
            <h4 className="font-semibold text-purple-300 text-lg">🎯 特殊功能</h4>
            <div>
              <h5 className="font-medium text-white">Bilibili 视频数据 (bilibili)</h5>
              <p className="text-sm text-gray-400">监控 Bilibili 视频数据</p>
            </div>
            <div>
              <h5 className="font-medium text-white">自定义 API (customapi)</h5>
              <p className="text-sm text-gray-400">集成自定义 API 数据</p>
            </div>
          </div>
        </div>

        {/* 媒体服务器小部件功能说明 */}
        <div className="mt-6 bg-green-900/20 border border-green-500/30 rounded-lg p-4">
          <h4 className="font-medium text-green-300 mb-3">📺 媒体服务器小部件功能</h4>
          <div className="text-sm text-green-200 space-y-3">
            <p>
              <strong>全新升级</strong>：根据 Homepage 官方文档，媒体服务器小部件现在支持丰富的功能配置选项。
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
              <div>
                <h5 className="font-medium text-green-300 mb-2">🎛️ 可配置功能：</h5>
                <ul className="space-y-1">
                  <li>• <strong>块状显示</strong> - 以块状布局显示媒体信息</li>
                  <li>• <strong>正在播放</strong> - 显示当前播放的媒体内容</li>
                  <li>• <strong>用户信息</strong> - 显示用户相关信息</li>
                  <li>• <strong>媒体控制</strong> - 启用播放控制按钮</li>
                  <li>• <strong>剧集编号</strong> - 显示电视剧的剧集编号</li>
                  <li>• <strong>单流扩展</strong> - 将单个流扩展为两行显示</li>
                </ul>
              </div>
              <div>
                <h5 className="font-medium text-green-300 mb-2">🎯 支持的服务器：</h5>
                <ul className="space-y-1">
                  <li>• <strong>EMBY</strong> - 支持原生小部件模式</li>
                  <li>• <strong>Jellyfin</strong> - 完整功能支持</li>
                  <li>• <strong>Plex</strong> - 完整功能支持</li>
                </ul>
                <div className="mt-3 p-2 bg-green-800/30 rounded">
                  <p className="text-xs text-green-300">
                    💡 <strong>提示</strong>：所有功能都可以通过多选框独立开启或关闭，
                    让您完全自定义小部件的显示内容和行为。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 服务编辑模态框 */}
      <ServiceEditModal
        isOpen={showServiceModal}
        onClose={() => {
          setShowServiceModal(false)
          setEditingService(null)
        }}
        onSave={handleSaveService}
        service={editingService?.service}
        groupName={editingService?.groupName || ''}
        availableGroups={availableGroups}
      />

      {/* 分组编辑模态框 */}
      <GroupEditModal
        isOpen={showGroupModal}
        onClose={() => {
          setShowGroupModal(false)
          setEditingGroup(null)
        }}
        onSave={handleSaveGroup}
        group={editingGroup}
        existingGroups={availableGroups}
        groupLayout={getCurrentGroupLayout(editingGroup)}
      />
    </div>
  )
}

export default ServicesConfig
